import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { v2ConnectorsService } from '../services/v2ConnectorsService';
import { connectorTypesService } from '../services/connectorTypesService';
import marketplaceService from '../services/marketplaceService';
import marketplaceExtrasService from '../services/marketplaceExtrasService';
import ConnectorFiles from './ConnectorFiles';
import ConnectorTest from './ConnectorTest';
import './Admin.css';

const V2ConnectorForm = ({ mode = 'create' }) => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [formData, setFormData] = useState({
        name: '',
        v2_connector_type_id: '',
        marketplace_id: '',
        marketplace_module_ids: [],
        entrypoint: ''
    });
    const [connectorTypes, setConnectorTypes] = useState([]);
    const [marketplaces, setMarketplaces] = useState([]);
    const [marketplaceModules, setMarketplaceModules] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [connector, setConnector] = useState(null);
    const [activeTab, setActiveTab] = useState('details');

    useEffect(() => {
        loadConnectorTypes();
        loadMarketplaces();
        if (mode !== 'create' && id) {
            loadConnector();
        }
    }, [id, mode]);

    // Show the modules defined for the chosen marketplace.
    useEffect(() => {
        if (!formData.marketplace_id) { setMarketplaceModules([]); return; }
        marketplaceExtrasService.modules.list(formData.marketplace_id)
            .then(res => setMarketplaceModules(res.modules || []))
            .catch(() => setMarketplaceModules([]));
    }, [formData.marketplace_id]);

    const loadConnectorTypes = async () => {
        try {
            const response = await connectorTypesService.list();
            setConnectorTypes(response.connectorTypes || []);
        } catch (err) {
            setError('Failed to load connector types: ' + err.message);
        }
    };

    const loadMarketplaces = async () => {
        try {
            const res = await marketplaceService.getCatalog();
            const list = res.marketplaces || res.settings || [];
            setMarketplaces(list.filter(m => m.MARKETPLACEID > 0 && m.SYSTEMACTIVE === 'Y'));
        } catch (err) {
            // non-fatal — the dropdown just stays empty
        }
    };

    const loadConnector = async () => {
        try {
            setLoading(true);
            const response = await v2ConnectorsService.get(id);
            setConnector(response.connector);
            setFormData({
                name: response.connector.name || '',
                v2_connector_type_id: response.connector.v2_connector_type_id || '',
                marketplace_id: response.connector.marketplace_id || '',
                marketplace_module_ids: response.connector.marketplace_module_ids
                    ? String(response.connector.marketplace_module_ids).split(',').filter(Boolean)
                    : [],
                entrypoint: response.connector.entrypoint || ''
            });
            setError(null);
        } catch (err) {
            setError('Failed to load connector: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            if (mode === 'create') {
                await v2ConnectorsService.create(formData);
            } else {
                await v2ConnectorsService.update(id, formData);
            }
            navigate('/admin/v2-connectors');
        } catch (err) {
            setError(`Failed to ${mode} connector: ` + err.message);
        } finally {
            setLoading(false);
        }
    };

    // Marketplace applies to Polling / Delivery / Transformation connectors.
    const typeName = (id) => {
        const t = connectorTypes.find(ct => String(ct.id) === String(id));
        return t ? t.name : '';
    };
    const marketplaceApplicable = ['Polling', 'Delivery', 'Transformation'].includes(typeName(formData.v2_connector_type_id));

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const next = { ...prev, [name]: value };
            // Clear the marketplace (and modules) when switching to a type it doesn't apply to.
            if (name === 'v2_connector_type_id' && !['Polling', 'Delivery', 'Transformation'].includes(typeName(value))) {
                next.marketplace_id = '';
                next.marketplace_module_ids = [];
            }
            // Changing the marketplace invalidates the previously chosen modules.
            if (name === 'marketplace_id') {
                next.marketplace_module_ids = [];
            }
            return next;
        });
    };

    const toggleModule = (moduleId) => {
        setFormData(prev => {
            const ids = prev.marketplace_module_ids.map(String);
            const key = String(moduleId);
            return { ...prev, marketplace_module_ids: ids.includes(key) ? ids.filter(x => x !== key) : [...ids, key] };
        });
    };

    if (loading && mode !== 'create') {
        return <div className="loading">Loading...</div>;
    }

    if (mode === 'view' && connector) {
        return (
            <div className="admin-container">
                <div className="admin-header">
                    <h2>View Connector</h2>
                    <div>
                        <button onClick={() => navigate(`/admin/v2-connectors/${id}/edit`)} className="btn btn-primary">
                            Edit
                        </button>
                        <button onClick={() => navigate('/admin/v2-connectors')} className="btn btn-secondary">
                            Back to List
                        </button>
                    </div>
                </div>

                <div className="view-form">
                    <div className="form-group">
                        <label>ID:</label>
                        <div className="view-value">{connector.id}</div>
                    </div>
                    <div className="form-group">
                        <label>Name:</label>
                        <div className="view-value">{connector.name}</div>
                    </div>
                    <div className="form-group">
                        <label>Connector Type:</label>
                        <div className="view-value">{connector.connector_type_name || '-'}</div>
                    </div>
                    <div className="form-group">
                        <label>Marketplace:</label>
                        <div className="view-value">{connector.marketplace_name || '-'}</div>
                    </div>
                    <div className="form-group">
                        <label>Marketplace Modules:</label>
                        <div className="view-value">{connector.marketplace_module_names || '-'}</div>
                    </div>
                    <div className="form-group">
                        <label>Created:</label>
                        <div className="view-value">{new Date(connector.created_at).toLocaleString()}</div>
                    </div>
                    <div className="form-group">
                        <label>Updated:</label>
                        <div className="view-value">{new Date(connector.updated_at).toLocaleString()}</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-container">
            <div className="admin-header">
                <h2>{mode === 'create' ? 'Create New Connector' : 'Edit Connector'}</h2>
                <button onClick={() => navigate('/admin/v2-connectors')} className="btn btn-secondary">
                    Cancel
                </button>
            </div>

            {error && <div className="error">{error}</div>}

            <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #e5e7eb', marginBottom: 16 }}>
                {[['details', 'Details'], ['code', 'Code'], ['test', 'Test']].map(([t, label]) => (
                    <button key={t} type="button" onClick={() => setActiveTab(t)}
                        style={{ padding: '0.5rem 1rem', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600, color: activeTab === t ? '#4a6cf7' : '#6b7280', borderBottom: activeTab === t ? '2px solid #4a6cf7' : '2px solid transparent', marginBottom: -1 }}>
                        {label}
                    </button>
                ))}
            </div>

            <form onSubmit={handleSubmit} className="admin-form">
                {activeTab === 'details' && (
                  <>
                <div className="form-group">
                    <label htmlFor="name">Name *</label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        disabled={loading}
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="v2_connector_type_id">Connector Type *</label>
                    <select
                        id="v2_connector_type_id"
                        name="v2_connector_type_id"
                        value={formData.v2_connector_type_id}
                        onChange={handleChange}
                        required
                        disabled={loading}
                    >
                        <option value="">Select a connector type...</option>
                        {connectorTypes.map(type => (
                            <option key={type.id} value={type.id}>
                                {type.name}
                            </option>
                        ))}
                    </select>
                </div>

                {marketplaceApplicable && (
                    <div className="form-group">
                        <label htmlFor="marketplace_id">Marketplace</label>
                        <select
                            id="marketplace_id"
                            name="marketplace_id"
                            value={formData.marketplace_id}
                            onChange={handleChange}
                            disabled={loading}
                        >
                            <option value="">— None —</option>
                            {marketplaces.map(m => (
                                <option key={m.MARKETPLACEID} value={m.MARKETPLACEID}>
                                    {m.SYSTEMNAME}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {marketplaceApplicable && formData.marketplace_id && (
                    <div className="form-group">
                        <label>Marketplace Modules</label>
                        {marketplaceModules.length === 0 ? (
                            <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>
                                No modules defined for this marketplace.
                            </p>
                        ) : (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {marketplaceModules.map(m => {
                                    const checked = formData.marketplace_module_ids.map(String).includes(String(m.id));
                                    return (
                                        <label key={m.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0.3rem 0.6rem', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem', background: checked ? '#eef2ff' : '#fff' }}>
                                            <input type="checkbox" checked={checked} disabled={loading} onChange={() => toggleModule(m.id)} />
                                            {m.name}
                                        </label>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
                  </>
                )}

                {activeTab === 'code' && (
                <div className="form-group">
                    <label>Connector Code</label>
                    <p style={{ color: '#6b7280', fontSize: '0.8rem', margin: '0 0 8px' }}>
                        The code belongs to the marketplace and is shared across its connectors. Name this connector’s entry point below (a file in the shared code), then Save.
                    </p>
                    <div className="form-group" style={{ maxWidth: 420 }}>
                        <label htmlFor="entrypoint">Entry point for the Connector</label>
                        <input
                            type="text"
                            id="entrypoint"
                            name="entrypoint"
                            value={formData.entrypoint}
                            onChange={handleChange}
                            disabled={loading}
                            placeholder="e.g. rmsPoller.js"
                        />
                    </div>
                    <ConnectorFiles
                        connectorId={mode !== 'create' ? id : null}
                        marketplaceId={formData.marketplace_id}
                        entrypoint={formData.entrypoint}
                        assistantContext={{
                            name: formData.name,
                            connectorType: typeName(formData.v2_connector_type_id),
                            marketplaceId: formData.marketplace_id,
                            marketplaceName: (marketplaces.find(m => String(m.MARKETPLACEID) === String(formData.marketplace_id)) || {}).SYSTEMNAME,
                            moduleName: marketplaceModules.filter(m => formData.marketplace_module_ids.map(String).includes(String(m.id))).map(m => m.name).join(', '),
                        }}
                    />
                </div>
                )}

                {activeTab === 'test' && (
                    <div className="form-group">
                        <label>Test Connector</label>
                        <ConnectorTest connectorId={mode !== 'create' ? id : null} marketplaceId={formData.marketplace_id} entrypoint={formData.entrypoint} />
                    </div>
                )}

                {activeTab !== 'test' && (
                <div className="form-actions">
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                        {loading ? 'Saving...' : mode === 'create' ? 'Create Connector' : 'Update Connector'}
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate('/admin/v2-connectors')}
                        className="btn btn-secondary"
                        disabled={loading}
                    >
                        Cancel
                    </button>
                </div>
                )}
            </form>
        </div>
    );
};

export default V2ConnectorForm;
